import { Stack, SxProps, Theme, Typography } from '@mui/material';
import type { ConversationTriageState, TriageTool } from '@provision/entity-types';
import { ChevronDown, Sparkles, Unplug, Zap } from 'lucide-react';
import { KeyboardEvent, useEffect, useMemo, useRef } from 'react';
import { EllipsesMenu, EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { fieldBackground } from 'src/components/ExpandableTextArea/field-background';
import { SelectMenu } from 'src/components/SelectMenu';
import { SelectMenuOption } from 'src/components/SelectMenu/select-menu.types';
import { summarizeTriageToolModes } from 'src/graphql/entities/conversation-triage/conversation-triage.mappings';
import {
  SelectorDefaultBadge,
  SelectorOptionMeta,
  SelectorOptionMetaDot,
  SelectorOptionUsage,
  SelectorRenameInput,
  SelectorRow,
  SelectorTriggerBox,
  SelectorTriggerChevron,
  SelectorTriggerIcon,
  SelectorTriggerName,
} from './triage-config-selector.elements';

const FALLBACK_CONFIG_NAME = 'Default configuration';
const SEARCH_OPTION_THRESHOLD = 8;
const NOT_LINKED_HINT = 'Not linked to a behavior yet';
const FALLBACK_BEHAVIOR_NAME = 'Untitled behavior';
// The subline shares one 11px row with the tool-mode summary, so a configuration
// pinned by many behaviors is summarised rather than listed in full.
const MAX_LISTED_BEHAVIORS = 2;
const NEW_CONFIG_ACTION_LABEL = 'New configuration';
// The closing EllipsesMenu (a MUI Menu/Modal) keeps its FocusTrap enforcing
// focus for the length of its exit transition, so an `autoFocus` on the
// input mounted in the same tick loses the race and gets yanked back. Tried:
// `autoFocus` directly — works in jsdom (no real transition) but not in the
// browser. Deferring past the transition avoids the trap.
// See https://github.com/mui/material-ui/issues/33004
const RENAME_INPUT_FOCUS_DELAY_MS = 200;

const triageConfigActionsButtonSx: SxProps<Theme> = {
  width: '2.25rem',
  height: '2.375rem',
  borderRadius: '0.5625rem',
  border: (theme) => `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  backgroundColor: (theme) => fieldBackground(theme),
  flexShrink: 0,
  '&:hover': {
    borderColor: (theme) => theme.palette.custom.OV.sys.outline,
    backgroundColor: (theme) => fieldBackground(theme),
  },
};

export interface TriageConfigSelectorProps {
  configurations: ConversationTriageState[];
  selectedId: string | null;
  defaultId: string | null;
  renamingId: string | null;
  tools: TriageTool[];
  defaultVisibility: string;
  disabled?: boolean;
  /**
   * No row exists yet (platform-default placeholder shown). Disables the ⋮
   * actions and hides the create row: in that state the placeholder already is
   * the configuration about to be created, and offering a second create lets
   * two `isDefault` inserts race for the same partner.
   */
  actionsDisabled?: boolean;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onDuplicate: (id: string) => void;
  onRenameStart: (id: string) => void;
  onRenameCommit: (id: string, name: string) => void;
  onRenameCancel: () => void;
  onSetDefault: (id: string) => void;
  onDelete: (id: string) => void;
}

const configLabel = (config: ConversationTriageState) => config.name ?? FALLBACK_CONFIG_NAME;

const behaviorUsageLabel = (
  usedByBehaviors: ConversationTriageState['usedByBehaviors'],
): string => {
  const names = usedByBehaviors.map((behavior) => behavior.name ?? FALLBACK_BEHAVIOR_NAME);
  const listed = names.slice(0, MAX_LISTED_BEHAVIORS).join(', ');
  const remaining = names.length - MAX_LISTED_BEHAVIORS;

  return remaining > 0 ? `${listed} +${remaining} more` : listed;
};

export const TriageConfigSelector = ({
  configurations,
  selectedId,
  defaultId,
  renamingId,
  tools,
  defaultVisibility,
  disabled = false,
  actionsDisabled = false,
  onSelect,
  onCreate,
  onDuplicate,
  onRenameStart,
  onRenameCommit,
  onRenameCancel,
  onSetDefault,
  onDelete,
}: TriageConfigSelectorProps) => {
  const renameInputRef = useRef<HTMLInputElement>(null);
  const selectedConfig =
    configurations.find((config) => config.conversationTriageId === selectedId) ?? null;
  const renamingConfig =
    configurations.find((config) => config.conversationTriageId === renamingId) ?? null;
  const isSelectedDefault = selectedConfig?.conversationTriageId === defaultId;

  const renamingConfigId = renamingConfig?.conversationTriageId ?? null;
  useEffect(() => {
    if (!renamingConfigId) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      renameInputRef.current?.focus();
    }, RENAME_INPUT_FOCUS_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [renamingConfigId]);

  const options = useMemo<SelectMenuOption[]>(
    () =>
      configurations.map((config) => {
        const toolSummary = summarizeTriageToolModes(
          config.actionConfig,
          tools,
          defaultVisibility,
        );
        return {
          value: config.conversationTriageId,
          title: configLabel(config),
          description: (
            <SelectorOptionMeta>
              <SelectorOptionUsage>
                {config.usedByBehaviors.length ? <Zap /> : <Unplug />}
                {config.usedByBehaviors.length
                  ? behaviorUsageLabel(config.usedByBehaviors)
                  : NOT_LINKED_HINT}
              </SelectorOptionUsage>
              {toolSummary ? (
                <>
                  <SelectorOptionMetaDot />
                  <span>{toolSummary}</span>
                </>
              ) : null}
            </SelectorOptionMeta>
          ),
          endIcon:
            config.conversationTriageId === defaultId ? (
              <SelectorDefaultBadge data-testid="triage-config-default-badge">
                Default
              </SelectorDefaultBadge>
            ) : undefined,
        };
      }),
    [configurations, tools, defaultVisibility, defaultId],
  );

  const commitRename = () => {
    if (!renamingConfig) {
      return;
    }
    const value = renameInputRef.current?.value.trim() ?? '';
    if (value) {
      onRenameCommit(renamingConfig.conversationTriageId, value);
    } else {
      onRenameCancel();
    }
  };

  const handleRenameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitRename();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onRenameCancel();
    }
  };

  const actionsOptions = useMemo<EllipsesMenuOption[]>(
    () =>
      selectedConfig && !actionsDisabled
        ? [
            {
              label: 'Rename',
              icon: 'pencil',
              iconLibrary: 'lucide',
              onClick: () => onRenameStart(selectedConfig.conversationTriageId),
            },
            {
              label: 'Duplicate',
              icon: 'copy',
              iconLibrary: 'lucide',
              onClick: () => onDuplicate(selectedConfig.conversationTriageId),
            },
            ...(isSelectedDefault
              ? []
              : [
                  {
                    label: 'Set as default',
                    icon: 'star',
                    iconLibrary: 'lucide' as const,
                    onClick: () => onSetDefault(selectedConfig.conversationTriageId),
                  },
                ]),
            {
              label: 'Delete',
              icon: 'trash-2',
              iconLibrary: 'lucide',
              danger: true,
              disabled: isSelectedDefault,
              onClick: () => onDelete(selectedConfig.conversationTriageId),
            },
          ]
        : [],
    [
      selectedConfig,
      actionsDisabled,
      isSelectedDefault,
      onRenameStart,
      onDuplicate,
      onSetDefault,
      onDelete,
    ],
  );

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" fontWeight={700} color="custom.OV.sys.onBackground">
        Configurations
      </Typography>
      <SelectorRow>
        {renamingConfig ? (
          <SelectorRenameInput
            key={renamingConfig.conversationTriageId}
            inputRef={renameInputRef}
            defaultValue={renamingConfig.name ?? ''}
            placeholder={FALLBACK_CONFIG_NAME}
            onBlur={commitRename}
            onKeyDown={handleRenameKeyDown}
            inputProps={{ 'aria-label': 'Configuration name' }}
          />
        ) : (
          <SelectMenu
            label="Triage configuration"
            options={options}
            value={selectedId}
            onChange={(value) => onSelect(value)}
            search={configurations.length >= SEARCH_OPTION_THRESHOLD}
            searchPlaceholder="Search configurations…"
            disabled={disabled}
            menuWidth="trigger"
            createAction={
              actionsDisabled
                ? undefined
                : { label: NEW_CONFIG_ACTION_LABEL, onSelect: onCreate }
            }
            renderTrigger={({ getTriggerProps }) => (
              <SelectorTriggerBox
                {...getTriggerProps()}
                aria-label={`Triage configuration: ${
                  selectedConfig ? configLabel(selectedConfig) : FALLBACK_CONFIG_NAME
                }`}
                data-testid="triage-config-trigger"
              >
                <SelectorTriggerIcon aria-hidden>
                  <Sparkles />
                </SelectorTriggerIcon>
                <SelectorTriggerName>
                  {selectedConfig ? configLabel(selectedConfig) : FALLBACK_CONFIG_NAME}
                </SelectorTriggerName>
                {isSelectedDefault ? (
                  <SelectorDefaultBadge data-testid="triage-config-default-badge">
                    Default
                  </SelectorDefaultBadge>
                ) : null}
                <SelectorTriggerChevron aria-hidden>
                  <ChevronDown />
                </SelectorTriggerChevron>
              </SelectorTriggerBox>
            )}
          />
        )}
        <EllipsesMenu
          triggerTestId="triage-config-actions-trigger"
          title="Configuration actions"
          icon="more-vertical"
          iconLibrary="lucide"
          disabled={actionsDisabled}
          sx={triageConfigActionsButtonSx}
          options={actionsOptions}
        />
      </SelectorRow>
    </Stack>
  );
};
